/**
 * Automatically appends UTM parameters to the current URL if they do not already exist.
 *
 * @description
 * This hook checks if the URL contains an `utm_campaign` parameter. If not, it extracts
 * the referring domain from `document.referrer` and constructs default UTM parameters
 * based on that referrer and the user's device. It then updates the browser's history
 * with the newly formed URL, avoiding a full page refresh.
 *
 * The fields map directly to the backend utm_keys list:
 * - utm_source    → origin of the traffic (e.g. 'organic_referrer')
 * - utm_medium    → medium/channel (e.g. 'organic_login', 'organic_register')
 * - utm_campaign  → referrer hostname
 * - utm_term      → keyword (preserved from URL if already present, e.g. from paid ads)
 * - utm_matchtype → keyword match type (preserved from URL if already present)
 * - referrer      → full referrer URL
 * - platform      → detected device platform ('mobile', 'tablet' or 'desktop')
 *
 * Usage example:
 * ```jsx
 * import { useAppendUtmParams } from './useAppendUtmParams';
 *
 * function MyComponent() {
 *   useAppendUtmParams();
 *
 *   return <div>My Page</div>;
 * }
 * ```
 */
import { useEffect } from 'react';

/**
 * Detects the user's platform based on the User-Agent string.
 * @returns {'mobile' | 'tablet' | 'desktop'}
 */
function detectPlatform() {
  const ua = navigator.userAgent;
  if (/tablet|ipad|playbook|silk/i.test(ua)) return 'tablet';
  if (/mobile|iphone|ipod|android|blackberry|mini|windows\sce|palm/i.test(ua)) return 'mobile';
  return 'desktop';
}

export function useAppendUtmParams(defaultMedium = 'organic_login') {
  useEffect(() => {
    const currentParams = new URLSearchParams(window.location.search);

    // If 'utm_campaign' is not present, append all UTM parameters using the referrer.
    if (!currentParams.has('utm_campaign')) {
      const referrer = document.referrer;
      if (referrer) {
        const utmCampaign = new URL(referrer).hostname;
        const utmSource = 'organic_referrer';
        const utmMedium = defaultMedium;
        const platform = detectPlatform();

        // utm_term and utm_matchtype are preserved from the URL if already set
        // (e.g. coming from paid campaigns). Otherwise they are not appended.
        const utmTerm = currentParams.get('utm_term');
        const utmMatchtype = currentParams.get('utm_matchtype');

        const newUrl = new URL(window.location.href);
        newUrl.searchParams.set('utm_source', utmSource);
        newUrl.searchParams.set('utm_medium', utmMedium);
        newUrl.searchParams.set('utm_campaign', utmCampaign);
        newUrl.searchParams.set('referrer', referrer);
        newUrl.searchParams.set('platform', platform);

        if (utmTerm) newUrl.searchParams.set('utm_term', utmTerm);
        if (utmMatchtype) newUrl.searchParams.set('utm_matchtype', utmMatchtype);

        window.history.replaceState({}, '', newUrl.toString());
      }
    }
  }, [defaultMedium]);
}



// WEBPACK FOOTER //
// ./src/views/Auth/Login/hooks/useAppendUtmParams.js